import camelcaseKeys from "camelcase-keys";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";

import { AXIOS } from "hooks/axios";
import { useAuth } from "components/auth/AuthProvider";
import { useError } from "components/status/ErrorProvider";
import { useSuccess } from "components/status/SuccessProvider";

export type FwupdatesSchema = {
  key: string;
  size: number;
  eTag: string;
  lastModified: string;
  storageClass: string;
  checksumAlgorithm: string;
};

export const usePavmFwupdates = () => {
  const { user } = useAuth();

  const uri = user?.project?.uri;

  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    FwupdatesSchema[]
  >(
    [`PAVM FWUPDATES ${uri}`],
    () => AXIOS.get<FwupdatesSchema[]>(`${uri}/fwupdates`),
    {
      retry: false,
      enabled: !!uri,
      staleTime: 1000 * 120,
      select: (data) => camelcaseKeys(data.data.data as any, { deep: true }),
    }
  );
};

type FwupdateBaseSchema = {
  name: string;
  hash: string;
  size: number;
  eTag: string;
  triggerAt: number;
  lastModified: string;
  checksumAlgorithm: string;
};

export type FwupdateSchema = {
  uploaded: FwupdateBaseSchema & {
    status:
      | "EMPTY"
      | "READY"
      | "REQUESTED"
      | "SCHEDULED"
      | "INSTALLING"
      | "INSTALLED";
  };
  installed: FwupdateBaseSchema;
};

export const usePavmFwupdateStatus = (host: string) => {
  const { user } = useAuth();
  const uri = user?.project?.uri;

  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    FwupdateSchema
  >(
    [`PAVM FWUPDATE STATUS ${uri} ${host}`],
    () => AXIOS.get<FwupdateSchema>(`${uri}/${host}/fwupdate/status`),
    {
      retry: 2,
      enabled: !!uri,
      staleTime: 1000 * 120,
      select: (data) => camelcaseKeys(data.data.data as any, { deep: true }),
    }
  );
};

export const usePavmFwupdateRemove = (host: string) => {
  const { user } = useAuth();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const status = usePavmFwupdateStatus(host);

  const uri = user?.project?.uri;

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>
  >(
    [`PAVM FWUPDATE REMOVE`],
    () => AXIOS.post(`${uri}/${host}/fwupdate/remove`),
    {
      onSuccess: (data) => {
        status.refetch();

        setSuccess("Fwupdate removed");

        return camelcaseKeys(data.data, { deep: true });
      },
      onError: (err) => {
        const message = err?.response?.data?.message ?? "";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};

type PavmFwupdateUploadSchema = {
  key: string;
  size: number;
  eTag: string;
  lastModified: string;
  checksumAlgorithm: string;
};

export const usePavmFwupdateUpload = (host: string) => {
  const { user } = useAuth();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const status = usePavmFwupdateStatus(host);

  const uri = user?.project?.uri;

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    PavmFwupdateUploadSchema
  >(
    [`PAVM FWUPDATE UPLOAD`],
    (file: PavmFwupdateUploadSchema) =>
      AXIOS.post(`${uri}/${host}/fwupdate/upload`, { file }),
    {
      onSuccess: (data) => {
        status.refetch();
        setSuccess("Fwupdate uploaded");
        return camelcaseKeys(data.data, { deep: true });
      },
      onError: (err) => {
        const message = err?.response?.data?.message ?? "";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};

// export const usePavmFwupdateInstall = (host: string) => {
//   const { user } = useAuth();
//   const { setError } = useError();
//   const { setSuccess } = useSuccess();

//   const status = usePavmFwupdateStatus(host);

//   const uri = user.project.uri;

//   return useMutation(
//     [`PAVM FWUPDATE INSTALL`],
//     ({ triggerAt }: { triggerAt: number }) =>
//       AXIOS.post(
//         `${uri}/${host}/fwupdate/install`,
//         { triggerAt }
//       ),
//     {
//       onSuccess: (data) => {
//         status.refetch();
//         setSuccess("Fwupdate scheduled for install");
//         return camelcaseKeys(data.data, { deep: true });
//       },
//       onError: (err) => {
//         const error = err?.response?.data?.error ?? "";
//         setError(error);
//       },
//     }
//   );
// };
